import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Combobox, Spinner, formatDatumZeit, statusInfo, toast } from '@elbklar/ui';
import { TextField } from '@/components/form/Felder';
import { customersApi } from '@/lib/api';
import { type CustomerStatus } from '@/types';
import { statusAuswahl, statusFehlerText, statusPhase } from './statusWechsel';
import { LostReasonModal } from './LostReasonModal';

interface StatusSelectProps {
  customerId: string;
  currentStatus: CustomerStatus;
  currentLostReason?: string | null;
  currentFollowUpAt?: string | null;
}

type UpdatePayload = {
  status: CustomerStatus;
  lost_reason?: string | null;
  follow_up_at?: string | null;
};

const isLostStatus = (s: CustomerStatus) => s === 'lost' || s === 'lead_lost';

/**
 * Statusauswahl auf einer Detailseite.
 *
 * Welche Status sie anbietet, leitet sich aus dem Datensatz ab und wird nicht
 * von der Seite uebergeben. Bis zum 29.10.2026 stand hier ein `variant`-Prop,
 * das die Seite setzte: `/leads/<id>` immer `"lead"`, `/customers/<id>` immer
 * `"kunde"`. Beide Seiten laden aber ueber `customersApi.get` jeden Datensatz,
 * ohne seine Phase zu pruefen — `/leads/<kunde-id>` bot damit einem echten
 * Kunden die Lead-Status an und holte ihn mit einem Klick in die Lead-Phase
 * zurueck, K-Nummer und `activated_at` blieben stehen. Das Prop ist deshalb
 * weg: die Seite kann die Phase nicht mehr falsch behaupten.
 */
export function StatusSelect({
  customerId,
  currentStatus,
  currentLostReason,
  currentFollowUpAt,
}: StatusSelectProps) {
  const qc = useQueryClient();
  const options = statusAuswahl(statusPhase(currentStatus)).map((s) => ({
    value: s,
    label: statusInfo(s).label,
  }));

  const [pendingStatus, setPendingStatus] = useState<CustomerStatus | null>(null);
  const [showLostModal, setShowLostModal] = useState(false);
  const [followUpValue, setFollowUpValue] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: UpdatePayload) =>
      customersApi.update(customerId, payload),
    onMutate: () => setFehler(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', customerId] });
      qc.invalidateQueries({ queryKey: ['leads', customerId] });
      setPendingStatus(null);
      setShowLostModal(false);
      setFollowUpValue('');
    },
    // Bis zum 29.10.2026 gab es diesen Zweig nicht: der Dialog schloss nur im
    // Erfolgsfall, und ein gescheiterter Wechsel war nicht zu sehen. Am selben
    // Tag um 08:55 scheiterten vier Wechsel an einem Kunden in drei Sekunden
    // (401, Anmeldung abgelaufen), der fuenfte 19 Sekunden spaeter gelang —
    // sichtbar war davon nichts. Die Meldung steht an der Auswahl und bleibt
    // stehen; der Toast erreicht den Menschen auch dann, wenn ein Sheet
    // (Verloren-Grund, Wiedervorlage) die Zeile verdeckt.
    onError: (err) => {
      const text = statusFehlerText(err);
      setFehler(text);
      toast.error(text);
    },
  });

  const handleChange = (value: string) => {
    const next = value as CustomerStatus;
    if (!next || next === currentStatus) return;

    if (isLostStatus(next)) {
      setPendingStatus(next);
      setShowLostModal(true);
      return;
    }

    if (next === 'interested_later') {
      setPendingStatus(next);
      return;
    }

    mutation.mutate({ status: next });
  };

  const handleLostSubmit = (reason: string) => {
    if (!pendingStatus) return;
    mutation.mutate({ status: pendingStatus, lost_reason: reason });
  };

  const handleLostCancel = () => {
    setShowLostModal(false);
    setPendingStatus(null);
  };

  const handleFollowUpSave = () => {
    if (!pendingStatus || !followUpValue) return;
    const iso = new Date(followUpValue).toISOString();
    mutation.mutate({ status: pendingStatus, follow_up_at: iso });
  };

  const handleFollowUpCancel = () => {
    setPendingStatus(null);
    setFollowUpValue('');
  };

  // The value shown in the select: pending transition preview, else current.
  const selectValue = pendingStatus ?? currentStatus;
  const showFollowUpInput = pendingStatus === 'interested_later';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="min-w-[220px]">
          <Combobox
            aria-label="Status"
            options={options}
            value={selectValue}
            onChange={handleChange}
            placeholder="Status wählen"
            // Ohne `label` zeichnet die Combobox kein Field und damit keinen
            // Fehlertext (combobox.tsx: `if (!label) return ...`) — `error`
            // setzt hier allein `aria-invalid`. Das ist gewollt: die Meldung
            // steht einmal darunter (role="alert"), nicht zweimal.
            error={fehler ?? undefined}
            disabled={mutation.isPending}
          />
        </div>
        {mutation.isPending && (
          <span className="inline-flex items-center gap-2 t-small text-muted-foreground">
            <Spinner size={14} label="Status wird geändert" />
            Speichert
          </span>
        )}
      </div>

      {fehler && (
        <p role="alert" className="t-small text-danger">
          {fehler}
        </p>
      )}

      {/* Follow-up datetime flow */}
      {showFollowUpInput && (
        <div className="flex items-center gap-2">
          <TextField
            type="datetime-local"
            aria-label="Wiedervorlage"
            required
            value={followUpValue}
            onChange={(e) => setFollowUpValue(e.target.value)}
            className="w-auto"
          />
          <Button size="sm" onClick={handleFollowUpSave} disabled={!followUpValue || mutation.isPending}>
            Speichern
          </Button>
          <Button size="sm" variant="ghost" onClick={handleFollowUpCancel}>
            Abbrechen
          </Button>
        </div>
      )}

      {/* Current meta info */}
      {currentStatus === 'interested_later' && currentFollowUpAt && !showFollowUpInput && (
        <div className="t-small text-muted-foreground">
          Wiedervorlage: {formatDatumZeit(currentFollowUpAt)}
        </div>
      )}
      {isLostStatus(currentStatus) && currentLostReason && (
        <div className="t-small text-muted-foreground">Grund: {currentLostReason}</div>
      )}

      <LostReasonModal
        open={showLostModal}
        onCancel={handleLostCancel}
        onSubmit={handleLostSubmit}
      />
    </div>
  );
}
